import React, { useEffect } from 'react';
import { RadioField, RadioInput } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { Radiobuttons as RadiobuttonsType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { getValidationErrors } from '../../validators/validator-mapping';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { isRequired } from '../../helpers/isRequired';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { Field } from '../../types/fields';
import { getValue } from '../values/Get';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';
import { trackInteract } from '../../track-by-submit/common/tracking';

const Radiobuttons = ({ values, elementId, caption, helpText, description }: RadiobuttonsType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const valueObj = Radiobuttons.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    const [fieldHasChangedAtLeastOnce, setFieldHasChangedAtLeastOnce] = React.useState(false);

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [valueObj.value]);

    const isRequiredField = isRequired(formConfig, elementId);

    const isValid = Radiobuttons.isValidField(formConfig, formValues, elementId, fieldHasChangedAtLeastOnce);

    return (
        <>
            {caption && <h6>{caption}</h6>}
            <RadioField
                value={valueObj.value}
                required={isRequiredField}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                    const value = evt.target.value;
                    const valueConfig = values.find((item) => item.value === value);
                    const label = valueConfig?.caption;
                    const valueID = valueConfig?.valueID;
                    setFormValue(elementId, { label, value, valueID });
                    setFieldHasChangedAtLeastOnce(true);
                    // Tracking for Carbucks-damageSize selection
                    if (elementId === 'damageSize' && formConfig) {
                        trackInteract(formConfig, [{ key: 'carbucks-claim-size', value }]);
                    }
                }}
                errorText={errorMessage}
                id={elementId}
                key={elementId}
                isValid={isValid}
                helpText={helpText}
                description={description}
            >
                {values.map((option, key) => (
                    <RadioInput key={key} label={option.caption} value={option.value} description={option.helpText} />
                ))}
            </RadioField>
        </>
    );
};

Radiobuttons.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as RadiobuttonsType;
    const { values, summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined) {
        return <></>;
    }

    const option = values.find((item) => item.value === value.value);

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={option?.caption} />;
};

Radiobuttons.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Radiobuttons.getValue(formValues, elementId);

    // @TODO
    //   - Better name for labelX
    const value = valueObj.value;
    const labelX = valueObj.label;

    const valueId = valueObj.valueID ?? { string: value };

    return getClaimFormSubmitValue('radiobuttons', elementId, label, labelX, valueId);
};

Radiobuttons.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    return formValues[elementId];
};

Radiobuttons.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Radiobuttons.getValue(formValues, elementId);
    if (value === undefined) {
        return { label: '', value: '' };
    }
    return value;
};

Radiobuttons.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const valueObj = Radiobuttons.getValue(formValues, elementId);

    // @TODO
    //   - FIX!
    const valueStr = JSON.stringify(valueObj);

    return valueStr;
};

Radiobuttons.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean | undefined => {
    const valueObject = getValue(formConfig, formValues, elementId);

    if (!valueObject) {
        return false;
    }

    const element: Field = getElementFromElementId(formConfig, elementId) as RadiobuttonsType;
    const { values } = element;
    const selectedOption = values.find((item) => item.value === valueObject.value);

    return selectedOption ? true : false;
};

Radiobuttons.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasChangedAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasChangedAtLeastOnce) {
        return undefined;
    }

    const value = Radiobuttons.getValue(formValues, elementId);

    // When the user has not typed anything,
    // undefined is returned to prevent
    // showing red errors all over as soon
    // as he sees the form
    if (value === '' || value === undefined) {
        return undefined;
    }

    const isValid = Radiobuttons.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Radiobuttons;
